import { useCallback, useEffect, useRef } from 'react';

/**
 * Тянущаяся граница между зонами консоли.
 *
 * СВОЁ, И ВОТ ПОЧЕМУ. У FlyonUI 2 нет ни разделителя панелей, ни раскладки с
 * изменяемыми колонками - в библиотеке нет компонента этого рода вовсе
 * (docs/DECISIONS.md, запись [2026-09-18] об оболочке консоли). Брать ради
 * трёх границ стороннюю библиотеку панелей значит тащить в overlay-бюджет и в
 * дерево зависимостей слой ради двухсот строк.
 *
 * Границы РАБОТАЮТ С КЛАВИАТУРЫ. Разделитель с ролью separator и значением -
 * штатный шаблон ARIA; без клавиатуры ширину колонки нельзя изменить вовсе,
 * а она у консоли эфира не украшение: в правой колонке живёт чат.
 *
 * Тянется НА POINTER-СОБЫТИЯХ с захватом указателя: мышь, перо и палец ведут
 * себя одинаково, а захват не теряет перетаскивание, если курсор ушёл за
 * пределы элемента - с узкой полосой в пять пикселей это происходит сразу.
 */
export interface ResizerProps {
  orientation: 'vertical' | 'horizontal';
  /** Текущее значение доли, 0..1. */
  value: number;
  /** Доля по указателю; вызывается на каждое движение. */
  onChange: (fraction: number) => void;
  /** Доступное имя: у трёх границ консоли оно разное. */
  label: string;
  /** Шаг стрелки в долях. */
  step?: number;
  /** Размер опорной оси в пикселях: ширина окна или высота колонки. */
  extent: number;
  /** Сдвиг начала опорной оси в пикселях (для горизонтальной границы). */
  origin?: number;
  /** Доля отсчитывается от правого края: правая колонка задаётся своей шириной. */
  fromEnd?: boolean;
  className?: string;
  /** Положение границы: доля переводится в проценты вызывающим. */
  style?: React.CSSProperties;
}

export function Resizer({
  orientation,
  value,
  onChange,
  label,
  step = 0.01,
  extent,
  origin = 0,
  fromEnd = false,
  className = '',
  style,
}: ResizerProps) {
  const ref = useRef<HTMLDivElement>(null);
  const dragging = useRef(false);

  const fractionFromPointer = useCallback(
    (event: PointerEvent | React.PointerEvent) => {
      if (extent <= 0) return value;
      const position = orientation === 'vertical' ? event.clientX : event.clientY - origin;
      const raw = position / extent;
      return fromEnd ? 1 - raw : raw;
    },
    [extent, fromEnd, orientation, origin, value],
  );

  const onPointerDown = useCallback(
    (event: React.PointerEvent<HTMLDivElement>) => {
      event.preventDefault();
      dragging.current = true;
      ref.current?.setPointerCapture(event.pointerId);
      onChange(fractionFromPointer(event));
    },
    [fractionFromPointer, onChange],
  );

  const onPointerMove = useCallback(
    (event: React.PointerEvent<HTMLDivElement>) => {
      if (!dragging.current) return;
      onChange(fractionFromPointer(event));
    },
    [fractionFromPointer, onChange],
  );

  const stop = useCallback((event: React.PointerEvent<HTMLDivElement>) => {
    dragging.current = false;
    if (ref.current?.hasPointerCapture(event.pointerId) === true) {
      ref.current.releasePointerCapture(event.pointerId);
    }
  }, []);

  /**
   * Пока граница тянется, выделение текста под курсором превращает движение в
   * подсветку половины экрана. Снимается на время перетаскивания, а не насовсем.
   */
  useEffect(() => {
    const onUp = () => {
      dragging.current = false;
      document.body.style.removeProperty('user-select');
      document.body.style.removeProperty('cursor');
    };
    document.addEventListener('pointerup', onUp);
    return () => {
      document.removeEventListener('pointerup', onUp);
      onUp();
    };
  }, []);

  const onKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    const back = orientation === 'vertical' ? 'ArrowLeft' : 'ArrowUp';
    const forward = orientation === 'vertical' ? 'ArrowRight' : 'ArrowDown';
    const direction = event.key === back ? -1 : event.key === forward ? 1 : 0;
    if (direction === 0) return;

    event.preventDefault();
    onChange(value + direction * step * (fromEnd ? -1 : 1));
  };

  return (
    <div
      ref={ref}
      role="separator"
      tabIndex={0}
      aria-label={label}
      aria-orientation={orientation}
      aria-valuenow={Math.round(value * 100)}
      aria-valuemin={0}
      aria-valuemax={100}
      data-console-resizer={label}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={stop}
      onPointerCancel={stop}
      onKeyDown={onKeyDown}
      style={style}
      className={[
        'group absolute z-30 touch-none',
        'focus-visible:outline-primary focus-visible:outline-2 focus-visible:outline-offset-0',
        orientation === 'vertical'
          ? 'top-0 bottom-0 w-1.5 -translate-x-1/2 cursor-col-resize'
          : 'right-0 left-0 h-1.5 -translate-y-1/2 cursor-row-resize',
        className,
      ].join(' ')}
    >
      {/* Видимая нить границы: тонкая в покое, заметная при наведении. */}
      <span
        aria-hidden="true"
        className={[
          'bg-foreground/10 group-hover:bg-primary/60 group-focus-visible:bg-primary block transition-colors',
          orientation === 'vertical' ? 'mx-auto h-full w-px' : 'my-auto h-px w-full',
        ].join(' ')}
      />
    </div>
  );
}
